import React, { useEffect, useState } from 'react';
import { X, User, Clock, CheckCircle, FileText, Building, Calendar, MapPin, ChevronDown, ChevronUp } from 'lucide-react';

interface ProfileData {
  role: string;
  tenure: string;
  coreTasks: string[];
}

interface ProfileSummary {
  executive_summary: string;
}

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  profileData: ProfileData;
  profileSummary: ProfileSummary | null;
fetchProfileSummary: () => void;
}

const ProfileModal: React.FC<ProfileModalProps> = ({
  isOpen,
  onClose,
  profileData,
  profileSummary,
  fetchProfileSummary
}) => {
  const [showFullSummary, setShowFullSummary] = useState(false);

  const [Summary,setSummary] = useState<ProfileSummary | null>(null);


  useEffect(() => {
    if (!profileSummary) {
      fetchProfileSummary();
    } else {
      setSummary(profileSummary);
    }
  },[profileSummary]);


  if (!isOpen) return null;

  // Format markdown content for better display
  const formatSummaryContent = (content: string) => {
    const sections = content.split('\n\n');
    return sections.map((section, index) => {
      if (section.startsWith('# ')) {
        return <h1 key={index} className="text-xl font-bold text-gray-900 mb-3">{section.replace('# ', '')}</h1>;
      } else if (section.startsWith('## ')) {
        return <h2 key={index} className="text-lg font-semibold text-gray-800 mb-2 mt-4">{section.replace('## ', '')}</h2>;
      } else if (section.startsWith('| ')) {
        // Format table
        const rows = section.split('\n').filter(row => row.trim());
        return (
          <div key={index} className="overflow-x-auto mb-3">
            <table className="min-w-full border border-gray-300 rounded-lg text-sm">
              <tbody>
                {rows.map((row, rowIndex) => {
                  if (row.includes('|---')) return null;
                  const cells = row.split('|').map(cell => cell.trim()).filter(cell => cell);
                  return (
                    <tr key={rowIndex} className={rowIndex === 0 ? "bg-gray-50" : "bg-white"}>
                      {cells.map((cell, cellIndex) => (
                        <td key={cellIndex} className={`px-3 py-2 border border-gray-200 ${rowIndex === 0 ? 'font-semibold text-gray-700' : 'text-gray-600'}`}>
                          {cell}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      } else if (section.startsWith('- **')) {
        // Format bullet points with bold headers
        const items = section.split('\n').filter(item => item.trim());
        return (
          <ul key={index} className="space-y-1 mb-3">
            {items.map((item, itemIndex) => {
              const match = item.match(/- \*\*(.*?):\*\* (.*)/);
              if (match) {
                return (
                  <li key={itemIndex} className="flex text-sm">
                    <span className="font-semibold text-gray-800 mr-2">{match[1]}:</span>
                    <span className="text-gray-600">{match[2]}</span>
                  </li>
                );
              }
              return <li key={itemIndex} className="text-gray-600 text-sm">{item.replace('- ', '')}</li>;
            })}
          </ul>
        );
      } else if (section.includes('---')) {
        return <hr key={index} className="my-4 border-gray-300" />;
      } else if (section.trim()) {
        return <p key={index} className="text-gray-700 leading-relaxed mb-3 text-sm">{section}</p>;
      }
      return null;
    }).filter(Boolean);
  };

  // Parse the markdown summary to extract key information
  const parseProfileSummary = (summary: string) => {
    if (!summary) return null;

    const lines = summary.split('\n');
    const sections: { [key: string]: string[] } = {};
    let currentSection = '';

    lines.forEach(line => {
      if (line.startsWith('##')) {
        currentSection = line.replace('##', '').trim();
        sections[currentSection] = [];
      } else if (currentSection && line.trim()) {
        sections[currentSection].push(line.trim());
      }
    });

    return sections;
  };

  const summaryData = profileSummary ? parseProfileSummary(profileSummary.executive_summary) : null;
  console.log('Parsed Summary Data:', profileSummary);

  // Extract key metrics from the summary
  const extractKeyMetrics = (sections: any) => {
    if (!sections) return null;

    const overview = sections['1. Employee Overview'] || [];
    const skills = sections['2. Key Skills and Expertise'] || [];

    return {
      name: overview.find(line => line.includes('Name'))?.split('|')[2]?.trim() || 'Employee',
      role: overview.find(line => line.includes('Role Title'))?.split('|')[2]?.trim() || profileData.role,
      tenure: overview.find(line => line.includes('Tenure'))?.split('|')[2]?.trim() || profileData.tenure,
      location: overview.find(line => line.includes('Primary Location'))?.split('|')[2]?.trim() || 'Not specified',
      businessUnit: overview.find(line => line.includes('Business Unit'))?.split('|')[2]?.trim() || 'Not specified',
      lastWorkingDay: overview.find(line => line.includes('Last Working Day'))?.split('|')[2]?.trim() || 'Not specified',
      keySkills: skills.filter(line => line.startsWith('- **')).map(line => line.replace('- **', '').split(':**')[0])
    };
  };

  const metrics = summaryData ? extractKeyMetrics(summaryData) : null;



  return (
    <div style={{zIndex:"999999"}} className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Profile Overview</h2>
              <p className="text-gray-600 text-sm">Your current profile information</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
            {/* Left Column - Quick Profile */}
      

            {/* Right Column - Detailed Profile */}
            <div className="lg:col-span-2">
              {/* AI Generated Summary */}
              {profileSummary && (
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold text-blue-900 flex items-center gap-2">
                      <FileText className="w-5 h-5" />
                      AI-Generated Profile Summary
                    </h3>
                    <button
                      onClick={() => setShowFullSummary(!showFullSummary)}
                      className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm"
                    >
                      {showFullSummary ? (
                        <>
                          <ChevronUp className="w-4 h-4" />
                          Show Less
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-4 h-4" />
                          Show Full Summary
                        </>
                      )}
                    </button>
                  </div>

                  {showFullSummary ? (
                    <div className="bg-white rounded-lg p-4 max-h-96 overflow-y-auto shadow-inner">
                      <div className="prose prose-sm max-w-none">
                        {formatSummaryContent(profileSummary.executive_summary)}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <p className="text-gray-700 leading-relaxed text-sm">
                        {metrics ? (
                          `${metrics.name} is a ${metrics.role} with ${metrics.tenure} of experience. 
                          Specializing in finance analytics and consulting, with expertise in bridging business and analytics teams.`
                        ) : (
                          "AI-generated profile summary shows your professional background and key competencies."
                        )}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Loading state if no summary */}
              {!profileSummary && (
                <div className="text-center py-8">
                  <div className="w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading profile summary...</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfileModal;